/**
 * Stripe Testing Utilities
 * Helper functions for testing Stripe integration
 */

export interface TestCard {
  number: string;
  description: string;
  expectedResult: string;
  type: 'success' | 'decline' | 'error';
  cvc?: string;
  exp_month?: number;
  exp_year?: number;
  postal_code?: string;
}

export interface WebhookEvent {
  type: string;
  description: string;
  mockData: any;
}

/**
 * Stripe test card numbers for different scenarios
 */
export const STRIPE_TEST_CARDS: TestCard[] = [
  // Success Cases
  {
    number: '4242424242424242',
    description: 'Visa - Success',
    expectedResult: 'Payment succeeds with no authentication',
    type: 'success',
    cvc: '123',
    exp_month: 12,
    exp_year: 2030,
    postal_code: '12345'
  },
  {
    number: '5555555555554444',
    description: 'Mastercard - Success',
    expectedResult: 'Payment succeeds with no authentication',
    type: 'success',
    cvc: '123',
    exp_month: 12,
    exp_year: 2030
  },
  {
    number: '378282246310005',
    description: 'American Express - Success',
    expectedResult: 'Payment succeeds with no authentication',
    type: 'success',
    cvc: '1234',
    exp_month: 12,
    exp_year: 2030
  },

  // 3D Secure Cases
  {
    number: '4000000000003220',
    description: 'Visa - 3D Secure Required',
    expectedResult: 'Triggers 3D Secure authentication',
    type: 'success',
    cvc: '123',
    exp_month: 12,
    exp_year: 2030
  },

  // Decline Cases
  {
    number: '4000000000000002',
    description: 'Visa - Generic Decline',
    expectedResult: 'Payment declined with generic_decline code',
    type: 'decline',
    cvc: '123',
    exp_month: 12,
    exp_year: 2030
  },
  {
    number: '4000000000009995',
    description: 'Visa - Insufficient Funds',
    expectedResult: 'Payment declined with insufficient_funds code',
    type: 'decline',
    cvc: '123',
    exp_month: 12,
    exp_year: 2030
  },
  {
    number: '4000000000000069',
    description: 'Visa - Expired Card',
    expectedResult: 'Payment declined with expired_card code',
    type: 'decline',
    cvc: '123',
    exp_month: 12,
    exp_year: 2030
  },
  {
    number: '4000000000000127',
    description: 'Visa - Incorrect CVC',
    expectedResult: 'Payment declined with incorrect_cvc code',
    type: 'decline',
    cvc: '123',
    exp_month: 12,
    exp_year: 2030
  },

  // Error Cases  
  {
    number: '4000000000000119',
    description: 'Visa - Processing Error',
    expectedResult: 'Payment fails with processing_error',
    type: 'error',
    cvc: '123',
    exp_month: 12,
    exp_year: 2030
  },
  {
    number: '4242424242424241',
    description: 'Visa - Invalid Number',
    expectedResult: 'Payment fails with invalid card number',
    type: 'error',
    cvc: '123',
    exp_month: 12,
    exp_year: 2030
  }
];

/**
 * Common webhook events to simulate
 */
export const WEBHOOK_EVENTS: WebhookEvent[] = [
  {
    type: 'invoice.payment_succeeded',
    description: 'Payment succeeds for subscription',
    mockData: {
      id: 'in_test_payment_succeeded',
      amount_paid: 2000, // $20.00
      currency: 'usd',
      status: 'paid',
      subscription: 'sub_test_subscription',
      customer: 'cus_test_customer',
      payment_intent: 'pi_test_payment_intent',
      created: Math.floor(Date.now() / 1000)
    }
  },
  {
    type: 'invoice.payment_failed',
    description: 'Payment fails for subscription',
    mockData: {
      id: 'in_test_payment_failed',
      amount_due: 2000,
      currency: 'usd',
      status: 'open',
      subscription: 'sub_test_subscription',
      customer: 'cus_test_customer',
      payment_intent: 'pi_test_failed_payment',
      created: Math.floor(Date.now() / 1000)
    }
  },
  {
    type: 'customer.subscription.created',
    description: 'New subscription is created',
    mockData: {
      id: 'sub_test_new_subscription',
      status: 'active',
      customer: 'cus_test_customer',
      current_period_start: Math.floor(Date.now() / 1000),
      current_period_end: Math.floor((Date.now() + 30 * 24 * 60 * 60 * 1000) / 1000),
      cancel_at_period_end: false,
      created: Math.floor(Date.now() / 1000)
    }
  },
  {
    type: 'customer.subscription.updated',
    description: 'Subscription is updated',
    mockData: {
      id: 'sub_test_updated_subscription',
      status: 'active',
      customer: 'cus_test_customer',
      current_period_start: Math.floor(Date.now() / 1000),
      current_period_end: Math.floor((Date.now() + 30 * 24 * 60 * 60 * 1000) / 1000),
      cancel_at_period_end: false,
      created: Math.floor((Date.now() - 24 * 60 * 60 * 1000) / 1000)
    }
  },
  {
    type: 'customer.subscription.deleted',
    description: 'Subscription is cancelled',
    mockData: {
      id: 'sub_test_deleted_subscription',
      status: 'canceled',
      customer: 'cus_test_customer',
      canceled_at: Math.floor(Date.now() / 1000),
      ended_at: Math.floor(Date.now() / 1000),
      created: Math.floor((Date.now() - 30 * 24 * 60 * 60 * 1000) / 1000)
    }
  }
];

/**
 * Test scenarios for different subscription flows
 */
export const TEST_SCENARIOS = {
  NEW_SUBSCRIPTION: {
    name: 'New Subscription Flow',
    description: 'Test complete new subscription creation',
    steps: [
      'Select a plan',
      'Complete checkout with success card',
      'Verify payment success page',
      'Check subscription status in billing'
    ]
  },
  PAYMENT_FAILURE: {
    name: 'Payment Failure Flow',
    description: 'Test subscription creation with failed payment',
    steps: [
      'Select a plan',
      'Use declined test card',
      'Verify error handling',
      'Check subscription status remains unchanged'
    ]
  },
  SUBSCRIPTION_UPDATE: {
    name: 'Subscription Update Flow',
    description: 'Test upgrading/downgrading subscription',
    steps: [
      'Have active subscription',
      'Select different plan',
      'Complete checkout',
      'Verify subscription changes'
    ]
  }
};

/**
 * Format amount for display (converts cents to dollars)
 */
export function formatAmount(cents: number, currency: string = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(cents / 100);
}

/**
 * Format timestamp for display
 */
export function formatTimestamp(timestamp: number): string {
  return new Date(timestamp * 1000).toLocaleString();
}

/**
 * Generate test customer ID
 */
export function generateTestCustomerId(): string {
  return `cus_test_${Date.now()}_${Math.random().toString(36).substring(7)}`;
}

/**
 * Generate test subscription ID
 */
export function generateTestSubscriptionId(): string {
  return `sub_test_${Date.now()}_${Math.random().toString(36).substring(7)}`;
}

/**
 * Generate test payment intent ID
 */
export function generateTestPaymentIntentId(): string {
  return `pi_test_${Date.now()}_${Math.random().toString(36).substring(7)}`;
}

/**
 * Validate Stripe publishable key format
 */
export function validateStripeKey(key: string): { valid: boolean; message: string } {
  if (!key) {
    return { valid: false, message: 'Stripe key is missing' };
  }
  
  if (key.startsWith('pk_test_')) {
    return { valid: true, message: 'Valid test key' };
  }
  
  if (key.startsWith('pk_live_')) {
    return { valid: true, message: 'Valid live key' };
  }
  
  return { valid: false, message: 'Invalid Stripe key format' };
}

/**
 * Get card brand from card number
 */
export function getCardBrand(cardNumber: string): string {
  const number = cardNumber.replace(/\s/g, '');
  
  if (/^4/.test(number)) return 'Visa';
  if (/^5[1-5]/.test(number)) return 'Mastercard';
  if (/^3[47]/.test(number)) return 'American Express';
  if (/^6/.test(number)) return 'Discover';
  
  return 'Unknown';
}

/**
 * Mask card number for display
 */
export function maskCardNumber(cardNumber: string): string {
  const number = cardNumber.replace(/\s/g, '');
  if (number.length < 4) return number;
  
  return `****-****-****-${number.slice(-4)}`;
}

/**
 * Check if environment is configured for Stripe
 */
import { getStripePublishableKey } from '../config/stripe';

export function checkStripeEnvironment(): {
  configured: boolean;
  issues: string[];
  recommendations: string[];
} {
  const issues: string[] = [];
  const recommendations: string[] = [];
  
  // Check publishable key
  const publishableKey = getStripePublishableKey();
  if (!publishableKey) {
    issues.push('Stripe publishable key is not set');
    recommendations.push('Add your Stripe publishable key to config');
  } else {
    const keyValidation = validateStripeKey(publishableKey);
    if (!keyValidation.valid) {
      issues.push(`Invalid publishable key: ${keyValidation.message}`);
    }
  }
  
  // Check if in test mode
  if (publishableKey && !publishableKey.startsWith('pk_test_')) {
    recommendations.push('Consider using test keys for development');
  }
  
  return {
    configured: issues.length === 0,
    issues,
    recommendations
  };
}

/**
 * Create mock webhook payload
 */
export function createMockWebhookPayload(eventType: string, data: any) {
  return {
    id: `evt_test_${Date.now()}_${Math.random().toString(36).substring(7)}`,
    object: 'event',
    api_version: '2023-10-16',
    created: Math.floor(Date.now() / 1000),
    data: {
      object: data
    },
    livemode: false,
    pending_webhooks: 1,
    request: {
      id: `req_test_${Math.random().toString(36).substring(7)}`,
      idempotency_key: null
    },
    type: eventType
  };
}

/**
 * Log Stripe test activity
 */
export class StripeTestLogger {
  private static logs: string[] = [];
  
  static log(message: string, data?: any): void {
    const timestamp = new Date().toLocaleTimeString();
    const logEntry = `[${timestamp}] ${message}`;
    
    console.log(logEntry, data);
    this.logs.push(logEntry);
    
    if (data) {
      this.logs.push(`   Data: ${JSON.stringify(data, null, 2)}`);
    }
  }
  
  static getLogs(): string[] {
    return [...this.logs];
  }
  
  static clearLogs(): void {
    this.logs = [];
  }
  
  static exportLogs(): string {
    return this.logs.join('\n');
  }
}

export default {
  STRIPE_TEST_CARDS,
  WEBHOOK_EVENTS,
  TEST_SCENARIOS,
  formatAmount,
  formatTimestamp,
  generateTestCustomerId,
  generateTestSubscriptionId,
  generateTestPaymentIntentId,
  validateStripeKey,
  getCardBrand,
  maskCardNumber,
  checkStripeEnvironment,
  createMockWebhookPayload,
  StripeTestLogger
};